/**
 * MentionPickerField - Seletor de pessoas para mencionar já na criação do andamento
 *
 * Controle compartilhado usado nos criadores de andamento (tarefas, negócios,
 * clientes e veículos). Permite escolher uma ou mais pessoas do tenant que
 * serão notificadas assim que o andamento for salvo.
 *
 * A persistência é feita pelas RPCs de criação do andamento (parâmetro
 * `p_mentioned_user_ids`), que criam o comentário de menção de forma atômica.
 *
 * @version 1.0.1
 * RELEASE: 2026-09-12 v1.0.1 - Correção da rolagem interna do popover: troca do
 *          ScrollArea por div nativa com overflow-y-auto e bloqueio de propagação
 *          de wheel/touch (modais capturam o evento e impediam rolar a lista).
 * RELEASE: 2026-09-12 v1.0.0 - Criação inicial. Reaproveita useTenantMembers e
 *          SmartAvatar; limite de 20 pessoas espelhando a validação do banco.
 */
import React, { useMemo, useState } from 'react';
import { AtSign, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useTenantMembers } from '@/hooks/useActivityComments';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/** Limite máximo de menções por andamento (mesmo teto validado no banco) */
export const MAX_MENTIONS_PER_ACTIVITY = 20;

/** Pessoa que pode ser mencionada */
export interface MentionableMember {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

/** Props do seletor de menções */
interface MentionPickerFieldProps {
  /** Pessoas atualmente marcadas */
  value: MentionableMember[];
  /** Callback de alteração da lista */
  onChange: (users: MentionableMember[]) => void;
  /** Desabilita a interação (ex.: enquanto salva) */
  disabled?: boolean;
  /** Rótulo exibido acima do controle */
  label?: string;
  /** Classe extra do container */
  className?: string;
}

/**
 * Campo "Mencionar pessoas": busca membros do tenant e exibe os escolhidos
 * como chips removíveis.
 */
export function MentionPickerField({
  value,
  onChange,
  disabled = false,
  label = 'Mencionar pessoas',
  className,
}: MentionPickerFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: members = [] } = useTenantMembers();

  // Lista filtrada pelo texto digitado (busca simples por nome)
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members as MentionableMember[];
    return (members as MentionableMember[]).filter((m) =>
      (m.full_name || '').toLowerCase().includes(term),
    );
  }, [members, search]);

  /** Alterna a seleção de uma pessoa */
  const toggleUser = (user: MentionableMember) => {
    const alreadySelected = value.some((u) => u.id === user.id);
    if (alreadySelected) {
      onChange(value.filter((u) => u.id !== user.id));
      return;
    }
    if (value.length >= MAX_MENTIONS_PER_ACTIVITY) {
      toast.error(`Você pode mencionar no máximo ${MAX_MENTIONS_PER_ACTIVITY} pessoas`);
      return;
    }
    onChange([...value, user]);
  };

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs font-medium flex items-center gap-2">
        <AtSign className="h-3.5 w-3.5" />
        {label}
        {value.length > 0 && (
          <span className="text-muted-foreground">({value.length})</span>
        )}
      </Label>

      <div className="flex flex-wrap items-center gap-1.5">
        {value.map((user) => (
          <Badge
            key={user.id}
            variant="secondary"
            className="gap-1 pl-1 pr-1.5 py-0.5 text-xs font-normal"
          >
            <SmartAvatar
              name={user.full_name}
              src={user.avatar_url}
              size="xs"
              className="h-4 w-4"
            />
            <span className="max-w-[140px] truncate">{user.full_name}</span>
            <button
              type="button"
              onClick={() => onChange(value.filter((u) => u.id !== user.id))}
              disabled={disabled}
              className="ml-0.5 text-muted-foreground hover:text-foreground"
              aria-label={`Remover ${user.full_name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={disabled}
            >
              <AtSign className="h-3.5 w-3.5 mr-1" />
              {value.length > 0 ? 'Adicionar' : 'Mencionar'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2 z-[200]" align="start">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar pessoa..."
              className="h-8 text-xs mb-2"
              maxLength={80}
            />
            {/* Lista rolável: div nativa com overflow-y-auto. O stopPropagation
                de wheel/touch evita que o modal pai capture o gesto de rolagem. */}
            <div
              className="max-h-56 overflow-y-auto overscroll-contain"
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              <div className="space-y-0.5 pr-1">
                {filtered.length === 0 && (
                  <p className="text-xs text-muted-foreground py-2 text-center">
                    Nenhuma pessoa encontrada
                  </p>
                )}
                {filtered.map((member) => {
                  const selected = value.some((u) => u.id === member.id);
                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => toggleUser(member)}
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs',
                        'hover:bg-muted transition-colors cursor-pointer',
                        selected && 'bg-muted',
                      )}
                    >
                      <SmartAvatar
                        name={member.full_name}
                        src={member.avatar_url}
                        size="xs"
                        className="h-5 w-5"
                      />
                      <span className="flex-1 truncate">{member.full_name}</span>
                      {selected && <Check className="h-3.5 w-3.5 text-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
